import React, {useState, useEffect} from "react";

// MUI COMPONENTS
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import MuiButton from "@mui/material/Button";

// COMPONENTS
import ContentBox from "@components/ContentBox/ContentBox";

// HELPERS
import api from "@helpers/api";
import {createClassName} from "@helpers/utils";
import ActivityIndicator from "../../../../../components/ActivityIndicator/ActivityIndicator.jsx";
import {
	getViewInitialStateForCustomer as GetViewInitialStateForCustomer_Recap
} from "./Recap.jsx";
import TextField from "../../../../../components/TextField/TextField.jsx";
import i18n from "../../../../../helpers/i18n.js";
import Validator from "../../../../../classes/Validator.js";
import TextView from "../../../../../components/TextView/TextView.jsx";

const RecapDownload = props =>
{
	const [loaded, setLoaded] = useState(false);
	const [customerEmail, setCustomerEmail] = useState(props.customer.getData("customer_email", ""));
	const [downloading, setDownloading] = useState(false);
	const [sending, setSending] = useState(false);
	const [summarySent, setSummarySent] = useState(false);
	const [summaryEmail, setSummaryEmail] = useState("");
	const [summaryEmailEdited, setSummaryEmailEdited] = useState(false);

	const downloadSummary = async () =>
	{
		if (downloading)
		{
			return false;
		}

		setDownloading(true);
		console.log("Downloading recap...");

		const rawDataInBase64 = await api(
			"/case/downloadSummary",
			{
				caseId,
				template: isCompany ? "recap/company.pdf" : "recap/person.pdf",
				frontpage: "frontpage.pdf",
				attachment: null
			},
			{responseType: "arraybuffer"},
			true
		);

		if (rawDataInBase64 !== "")
		{
			const file = new Blob([rawDataInBase64], {type: "application/pdf"});
			const fileURL = URL.createObjectURL(file);
			const link = document.createElement("a");

			let today = new Date();
			link.href = fileURL;
			link.download = "Recap " + today.toISOString().split('T')[0] + ".pdf";
			link.click();
		}
		else
		{
			console.log("Could not download case data");
		}
		setDownloading(false);
	};

	const sendSummary = async () =>
	{
		if (sending)
		{
			return false;
		}

		setSending(true);
		setSummarySent(false);
		console.log("Sending summary...");

		await api("/case/downloadSummary",
			{
				caseId,
				template: isCompany ? "recap/company.pdf" : "recap/person.pdf",
				frontpage: "frontpage.pdf",
				attachment: null,
				sendToEmail: customerEmail,
				emailBody: summaryEmail.length === 0 ? getDefaultText() : summaryEmail
			},
			{responseType: "arraybuffer"},
			true
		);

		setSending(false);
		setSummarySent(true);
	};

	const getDefaultText = () => {
		const {customer} = props;

		let greeting = "Hej\n\n";
		let name = customer.getData("customer_firstname", "");
		if (name.length > 0)
		{
			greeting = "Hej " + name + ",\n\n";
		}

		const text = "Tack för trevligt möte. Bifogat finner du en mötessammanfattning, hör gärna av dig om du har " +
			"frågor eller funderingar.\n\n" +
			"Önskar dig en fortsatt fin dag så ses vi på vårt uppföljningsmöte.\n\n"

		const signoff = "Bästa hälsningar,\n" + customer.getData("partner_fullname", "Fair Investments AB")

		return greeting + text + signoff;
	}

	useEffect(() =>
	{
		const {setAppState} = props;

		if (!loaded)
		{
			setAppState({
				viewFulfilled: true,
				showValidationWarning: false
			});
			setLoaded(true);
		}
	}, [props]);

	const {customer} = props;
	const {caseId, isCompany} = customer;
	const classes = createClassName("Cob-view", {});

	// To prevent emailing a summary that is incomplete to a customer, we check that the CustomerInformation view
	// has been completed before enabling the email form and button.
	const recapState = GetViewInitialStateForCustomer_Recap(customer);
	let recapFulfilled = true;
	if (recapState.customerState.customer_recap_correct_description.length <= 0 ||
		recapState.customerState.customer_recap_improvement.length <= 0 ||
		recapState.customerState.customer_recap_obstacles.length <= 0 ||
		recapState.customerState.customer_recap_relevant_needs.length <= 0 ||
		recapState.customerState.radio_customer_another_actor_since_last.length <= 0 ||
		(recapState.customerState.radio_customer_another_actor_since_last === "yes" &&
			(recapState.customerState.radio_customer_another_actor_positives.length <= 0 ||
			recapState.customerState.radio_customer_another_actor_improvements.length <= 0)
		) ||
		recapState.customerState.radio_customer_news_since_last.length <= 0 ||
		(recapState.customerState.radio_customer_news_since_last === "yes" &&
			(recapState.customerState.radio_customer_news_since_last_details.length <= 0)
		))
	{
		recapFulfilled = false;
	}

	let emailBodyForDisplay = "";
	if (summaryEmail && summaryEmail.length > 0)
	{
		emailBodyForDisplay = summaryEmail;
	}
	else if (!summaryEmailEdited)
	{
		emailBodyForDisplay = getDefaultText();
	}

	return (
		<div className={classes}>
			<ContentBox>
				<h2>Ladda ner / skicka Recap som PDF</h2>

				<Box sx={{
					width: "100%",
					display: "flex",
					flexDirection: "column",
					justifyContent: "flex-start",
					alignItems: "flex-start",
					mb: 2
				}}>
					<Box sx={{mb: 2}}>
						<Typography variant="subtitle1" color="primary.dark">
							Ladda ner en PDF med Recapfrågorna.
						</Typography>
					</Box>
					<MuiButton
						id="fade-button"
						variant="contained"
						onClick={downloadSummary}
						sx={{
							backgroundColor: "button.main",
							flexDirection: "row",
							boxShadow: "none",
							height: "32px",
							width: "140px",
							justifyContent: "center",
							"&:hover": {
								backgroundColor: "button.main",
								filter: "brightness(105%)",
								boxShadow: "none"
							}
						}}>
						{downloading ?
							(
								<ActivityIndicator scale={0.4} busy/>
							)
							:
							(
								<Typography
									variant="button"
									sx={{
										fontSize: "12px",
										fontWeight: 400,
										textTransform: "uppercase",
										textDecoration: "none"
									}}>
									Ladda ner PDF
								</Typography>
							)
						}
					</MuiButton>
				</Box>

				<Box sx={{
					width: "100%",
					display: "flex",
					flexDirection: "column",
					justifyContent: "flex-start",
					alignItems: "flex-start",
					mb: 2,
					borderTop: "1px dashed #DDD",
					paddingTop: "30px",
					marginTop: "20px"
				}}>
					<Box sx={{mb: 2}}>
						<Typography variant="subtitle1" color="primary.dark">
							Du kan även skicka recapfrågorna som PDF direkt till kund med e-post.
						</Typography>
					</Box>
					<TextField
						type="email"
						name="customer_dont_autocomplete_19"
						value={customerEmail}
						autocomplete="off"
						label={i18n("general", "email_address")}
						validation={Validator.validateEmail}
						onChange={({target}) => setCustomerEmail(target.value)}
						disabled={!recapFulfilled}
					/>

					<TextView
						label={`Meddelande`}
						value={emailBodyForDisplay}
						maxLength={1800}
						validation={value => value.length > 0}
						resize="vertical"
						onChange={({target}) => {
							setSummaryEmail(target.value);
							setSummaryEmailEdited(true);
						}}
						fullWidth
						minHeight={190}
						style={{marginTop: "10px"}}
						disabled={!recapFulfilled}
					/>

					<MuiButton
						id="fade-button"
						variant="contained"
						onClick={sendSummary}
						disabled={
							!recapFulfilled ||
							!Validator.validateEmail(customerEmail) ||
							(summaryEmailEdited && summaryEmail.length <= 0)
						}
						sx={{
							backgroundColor: "button.main",
							flexDirection: "row",
							justifyContent: "center",
							boxShadow: "none",
							height: "32px",
							width: "170px",
							marginTop: "15px",
							"&:hover": {
								backgroundColor: "button.main",
								filter: "brightness(105%)",
								boxShadow: "none"
							}
						}}>
						{sending ?
							(
								<ActivityIndicator scale={0.4} busy/>
							)
							:
							(
								<Typography
									variant="button"
									sx={{
										fontSize: "12px",
										fontWeight: 400,
										textTransform: "uppercase",
										textDecoration: "none"
									}}>
									Skicka som e-post
								</Typography>
							)
						}
					</MuiButton>

					{summarySent && (
						<Typography
							variant="button"
							sx={{
								fontSize: "13px",
								fontWeight: 400,
								textDecoration: "none",
								marginTop: "10px"
							}}>
							Recap skickades.
						</Typography>
					)}

					{!recapFulfilled && (
						<p style={{color: "#df4c43"}}>
							Avsnittet "Recap" måste fyllas i helt innan sammanfattningen kan mailas ut.
						</p>
					)}
				</Box>
			</ContentBox>
		</div>
	);
};

export default RecapDownload;